import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { clearPendingAiMessage, readPendingAiMessage, storePendingAiMessage } from '../ai/supportChatDeepLink';

type SupportChatContextValue = {
  open: boolean;
  pendingMessage: string | null;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  openChatWithMessage: (message: string) => void;
  clearPendingMessage: () => void;
};

const SupportChatContext = createContext<SupportChatContextValue | null>(null);

export const SupportChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(() => readPendingAiMessage());

  const openChat = useCallback(() => setOpen(true), []);
  const closeChat = useCallback(() => setOpen(false), []);
  const toggleChat = useCallback(() => setOpen((v) => !v), []);

  const clearPendingMessage = useCallback(() => {
    setPendingMessage(null);
    clearPendingAiMessage();
  }, []);

  const openChatWithMessage = useCallback((message: string) => {
    const trimmed = message.trim();
    if (!trimmed) {
      setOpen(true);
      return;
    }
    storePendingAiMessage(trimmed);
    setPendingMessage(trimmed);
    setOpen(true);
  }, []);

  const value = useMemo(
    () => ({
      open,
      pendingMessage,
      openChat,
      closeChat,
      toggleChat,
      openChatWithMessage,
      clearPendingMessage,
    }),
    [open, pendingMessage, openChat, closeChat, toggleChat, openChatWithMessage, clearPendingMessage]
  );

  return <SupportChatContext.Provider value={value}>{children}</SupportChatContext.Provider>;
};

export function useSupportChat(): SupportChatContextValue {
  const ctx = useContext(SupportChatContext);
  if (!ctx) {
    throw new Error('useSupportChat must be used within SupportChatProvider');
  }
  return ctx;
}
